import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, BookMarked } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import BookCard from "@/components/BookCard";
import ShareButton from "@/components/ShareButton";

export default function SharedRecommendation() {
    const { t } = useTranslation();
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [visibleCount, setVisibleCount] = useState(3);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await api.get(`/r/${id}`);
                if (!alive) return;
                setData(res.data);
                document.title = `RecomendaLibros · ${res.data.based_on}`;
            } catch (e) {
                if (!alive) return;
                const msg = formatApiError(e.response?.data?.detail);
                setError(msg || t("shared.error_title"));
                toast.error(msg || t("shared.error_title"));
            }
        })();
        return () => { alive = false; };
    }, [id, t]);

    if (error) {
        return (
            <main className="min-h-[calc(100vh-73px)] flex flex-col items-center justify-center px-6 text-center">
                <BookMarked size={32} strokeWidth={1} className="text-[var(--accent)] mb-4" />
                <p
                    className="font-display text-3xl text-[var(--ink)] mb-3"
                    style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 64' }}
                >
                    {t("shared.error_title")}
                </p>
                <p className="text-[var(--ink-muted)] mb-8">{t("shared.error_lead")}</p>
                <Link
                    to="/"
                    data-testid="shared-fallback-cta"
                    className="bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] transition-colors px-8 py-3 text-sm uppercase tracking-widest"
                >
                    {t("shared.error_cta")}
                </Link>
            </main>
        );
    }

    if (!data) {
        return (
            <main className="min-h-[calc(100vh-73px)] flex items-center justify-center">
                <p className="font-display text-2xl text-[var(--ink)] animate-slow-pulse italic">
                    {t("shared.loading")}
                </p>
            </main>
        );
    }

    const ogTitle = `RecomendaLibros · ${data.based_on}`;
    const titlesPreview = (data.recommendations || [])
        .slice(0, 3)
        .map((b) => `${b.title} — ${b.author}`)
        .join("; ");
    const ogDesc =
        t("shared.label") +
        ' "' +
        data.based_on +
        '"' +
        (titlesPreview ? `: ${titlesPreview}.` : "");

    // JSON-LD ItemList so Google indexes the 6 books as a structured list.
    // Served inside the SPA (Google renders JS), complements the
    // /api/seo/r/{id} pre-rendered HTML that non-JS scrapers fetch.
    const itemListLd = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: ogTitle,
        url: `${window.location.origin}/r/${data.id}`,
        numberOfItems: data.recommendations?.length || 0,
        itemListElement: (data.recommendations || []).map((b, i) => {
            const book = {
                "@type": "Book",
                name: b.title,
                author: { "@type": "Person", name: b.author },
                description: b.synopsis || undefined,
            };
            if (b.isbn) book.isbn = b.isbn;
            if (b.cover_url) book.image = b.cover_url;
            if (b.rating && b.rating > 0) {
                book.aggregateRating = {
                    "@type": "AggregateRating",
                    ratingValue: Number(b.rating.toFixed(2)),
                    bestRating: 5,
                };
            }
            return { "@type": "ListItem", position: i + 1, item: book };
        }),
    };

    return (
        <main className="min-h-[calc(100vh-73px)] max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-24">
            <Helmet>
                <title>{ogTitle}</title>
                <meta name="description" content={ogDesc} />
                <meta property="og:type" content="article" />
                <meta property="og:title" content={ogTitle} />
                <meta property="og:description" content={ogDesc} />
                <meta
                    property="og:image"
                    content={`${window.location.origin}/api/og/r/${data.id}.jpg`}
                />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:url" content={`${window.location.origin}/r/${data.id}`} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={ogTitle} />
                <meta name="twitter:description" content={ogDesc} />
                <meta
                    name="twitter:image"
                    content={`${window.location.origin}/api/og/r/${data.id}.jpg`}
                />
                <link rel="canonical" href={`${window.location.origin}/r/${data.id}`} />
                <script type="application/ld+json">
                    {JSON.stringify(itemListLd)}
                </script>
            </Helmet>

            <div className="border-b border-[var(--border)] pb-10 mb-12">
                <div className="flex items-center gap-2 mb-4">
                    <div className="h-px w-10 bg-[var(--accent)]/40" />
                    <span className="text-xs uppercase tracking-[0.3em] text-[var(--accent)]">
                        {t("shared.eyebrow")}
                    </span>
                </div>
                <p className="text-xs uppercase tracking-[0.3em] text-[var(--ink-muted)] mb-3">
                    {t("shared.label")}
                </p>
                <h1
                    data-testid="shared-title"
                    className="font-display text-5xl md:text-7xl text-[var(--ink)] tracking-tight italic"
                    style={{ fontVariationSettings: '"wght" 600, "wdth" 80, "opsz" 144, "slnt" -10' }}
                >
                    {data.based_on}
                </h1>
                <p className="text-[var(--ink-muted)] mt-3 text-lg">
                    {t(`preference_labels.${data.preference}`)}
                </p>

                <div className="flex flex-wrap items-center gap-3 mt-8">
                    <ShareButton recId={data.id} basedOn={data.based_on} />
                    <Link
                        to="/"
                        data-testid="shared-cta-home"
                        className="inline-flex items-center gap-2 bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] transition-colors px-5 py-2 text-xs uppercase tracking-widest"
                    >
                        <ArrowLeft size={13} strokeWidth={1.5} />
                        {t("shared.cta_home")}
                    </Link>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                {data.recommendations.slice(0, visibleCount).map((book, i) => (
                    <BookCard
                        key={book.id}
                        book={book}
                        index={i}
                        recommendationId={data.id}
                        readonly
                    />
                ))}
            </div>

            {data.recommendations.length > visibleCount && (
                <div className="flex justify-center mt-10">
                    <button
                        type="button"
                        data-testid="load-more-recs-btn"
                        onClick={() =>
                            setVisibleCount((n) =>
                                Math.min(n + 3, data.recommendations.length)
                            )
                        }
                        className="inline-flex items-center gap-2 px-6 py-3 text-xs uppercase tracking-widest border border-[var(--border-strong)] hover:border-[var(--ink)] hover:bg-[var(--bg-hover)] text-[var(--ink-muted)] hover:text-[var(--ink)] rounded-full transition-all"
                    >
                        {t("home.load_more", {
                            count: Math.min(3, data.recommendations.length - visibleCount),
                        })}
                    </button>
                </div>
            )}

            <p className="text-center text-xs text-[var(--ink-dim)] mt-16 max-w-xl mx-auto leading-relaxed">
                {t("shared.disclaimer")}
            </p>
        </main>
    );
}
